import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { SessionStorageService } from '../../../services/storage/session-storage.service';
import { LocalStorageService } from '../../../services/storage/local-storage.service';

import { Account, AccountGroup } from '../../../domain/account.domain';
import { PreferencesService } from '../../../services/preferences/preferences.service';

interface AccountResolver {
  resolveAccount(accounts: Array<Account>, accountId: string): Account;
}

interface AccountGroupResolver {
  resolveAccountGroup(accountGroups: Array<AccountGroup>, accountGroupId: string): AccountGroup;
}

export enum UserPreferenceStrategyType {
  LAST_SELECTED = 'LAST_SELECTED',
  DEFAULT_ACCOUNT = 'DEFAULT_ACCOUNT',
  ALWAYS_SELECT = 'ALWAYS_SELECT'
}

export type StrategyStyleType = 'default' | 'no-fallback' | 'last-selected' | 'all-accounts';

export enum UserPreferenceAccountNavigationKey {
  DEFAULT = 'ACCOUNT_NAVIGATION_STRATEGY',
  TRADING = 'MODAL_ACCOUNT_NAVIGATION_STRATEGY'
}

export class StrategyStyle {

  static isEnforceableStrategy(strategyStyle: StrategyStyleType): boolean {
    return strategyStyle === 'last-selected';
  }

  static getUserPreferenceKey(strategyStyle: StrategyStyleType): UserPreferenceAccountNavigationKey {
    return strategyStyle === 'no-fallback' || strategyStyle === 'all-accounts' ?
      UserPreferenceAccountNavigationKey.TRADING : UserPreferenceAccountNavigationKey.DEFAULT;
  }
}

class AccountResolverFactory {

  private accountStrategies: Map<StrategyStyleType, Map<UserPreferenceStrategyType, AccountResolver> | AccountResolver>;
  private accountGroupStrategies: Map<UserPreferenceStrategyType, AccountGroupResolver>;
  private readonly defaultStrategy: AccountResolver;

  constructor(private preferencesService: PreferencesService,
              accountContextStorage: AccountContextStorage) {
    const lastSelectedAccountResolver = new LastSelectedAccountResolver(accountContextStorage);
    const defaultAccountResolver =   new DefaultAccountResolver(accountContextStorage);
    const lastSelectedNoFallbackAccountResolver =   new LastSelectedNoFallbackAccountResolver(accountContextStorage);
    const defaultAccountNoFallbackAccountResolver =   new DefaultAccountNoFallbackAccountResolver(accountContextStorage);
    const alwaysSelectAccountResolver =   new AlwaysSelectAccountResolver(accountContextStorage);
    const allAccountsAccountResolver =   new AllAccountsAccountResolver(accountContextStorage);

    this.defaultStrategy = lastSelectedAccountResolver;

    this.accountStrategies = new Map<StrategyStyleType, Map<UserPreferenceStrategyType, AccountResolver> | AccountResolver>([
      ['last-selected', lastSelectedAccountResolver],
      ['no-fallback', new Map<UserPreferenceStrategyType, AccountResolver>([
        [UserPreferenceStrategyType.LAST_SELECTED, lastSelectedNoFallbackAccountResolver],
        [UserPreferenceStrategyType.DEFAULT_ACCOUNT, defaultAccountNoFallbackAccountResolver],
        [UserPreferenceStrategyType.ALWAYS_SELECT, alwaysSelectAccountResolver]
      ])],
      ['default', new Map<UserPreferenceStrategyType, AccountResolver>([
        [UserPreferenceStrategyType.LAST_SELECTED, lastSelectedAccountResolver],
        [UserPreferenceStrategyType.DEFAULT_ACCOUNT, defaultAccountResolver]
      ])],
      ['all-accounts', new Map<UserPreferenceStrategyType, AccountResolver>([
        [UserPreferenceStrategyType.LAST_SELECTED, allAccountsAccountResolver],
        [UserPreferenceStrategyType.DEFAULT_ACCOUNT, defaultAccountResolver],
        [UserPreferenceStrategyType.ALWAYS_SELECT, alwaysSelectAccountResolver]
      ])]
    ]);

    this.accountGroupStrategies = new Map<UserPreferenceStrategyType, AccountGroupResolver>( [
      [UserPreferenceStrategyType.LAST_SELECTED, lastSelectedAccountResolver],
      [UserPreferenceStrategyType.DEFAULT_ACCOUNT, defaultAccountResolver]
    ]);
  }

  getAccountResolver(strategyStyle: StrategyStyleType = 'default'): Observable<AccountResolver> {
    if (StrategyStyle.isEnforceableStrategy(strategyStyle)) {
      return of(this.getAccountResolverStrategy(strategyStyle));
    }
    const userPreferenceKey: UserPreferenceAccountNavigationKey = StrategyStyle.getUserPreferenceKey(strategyStyle);
    return this.preferencesService.getUserPreference<UserPreferenceStrategyType>(userPreferenceKey).pipe(
      map((type: UserPreferenceStrategyType): AccountResolver => this.getAccountResolverStrategy(strategyStyle, type))
    );
  }

  getAccountGroupResolver(): Observable<AccountGroupResolver> {
    return this.preferencesService.getUserPreference<UserPreferenceStrategyType>(UserPreferenceAccountNavigationKey.DEFAULT).pipe(
      map((type: UserPreferenceStrategyType): AccountGroupResolver => this.getAccountGroupResolverStrategy(type))
    );
  }

  private getAccountResolverStrategy(strategyStyle: StrategyStyleType,
                                     userPreferenceStrategyType: UserPreferenceStrategyType = undefined): AccountResolver {
    let accountStrategy: AccountResolver;
    const strategyOrMap: Map<UserPreferenceStrategyType, AccountResolver> | AccountResolver = this.accountStrategies.get(strategyStyle);
    if (strategyOrMap !== undefined) {
      if (this.isAccountResolver(strategyOrMap)) {
        accountStrategy = strategyOrMap as AccountResolver;
      } else if (userPreferenceStrategyType !== undefined) {
        accountStrategy = (strategyOrMap as Map<UserPreferenceStrategyType, AccountResolver>).get(userPreferenceStrategyType);
      }
    }
    return accountStrategy === undefined ? this.defaultStrategy : accountStrategy;
  }

  private getAccountGroupResolverStrategy(userPreferenceStrategyType: UserPreferenceStrategyType = undefined): AccountGroupResolver {
    userPreferenceStrategyType = userPreferenceStrategyType === undefined ? UserPreferenceStrategyType.DEFAULT_ACCOUNT : userPreferenceStrategyType;
    return this.accountGroupStrategies.get(userPreferenceStrategyType);
  }

  private isAccountResolver(object: Map<UserPreferenceStrategyType, AccountResolver> | AccountResolver): boolean { // TODO vpopov
    return 'resolveAccount' in object;
  }
}

class AccountContextStorage {

  private static readonly ACCOUNT_GROUP_ID_KEY: string = 'accountGroupId';
  private static readonly ACCOUNT_ID_KEY: string = 'accountId';
  private static readonly ALL_ACCOUNT_ID_KEY: string = 'allAccountId';
  private static readonly ALL_ACCOUNTS_ID: string = '$ALL';

  constructor(private sessionStorageService: SessionStorageService,
              private localStorageService: LocalStorageService) {
  }

  getSelectedAccountId(fromLocalStorage: boolean = false): string {
    if (fromLocalStorage === true) {
      return this.getLocalStorageAccountId();
    }
    return this.sessionStorageService.getItem<string>(AccountContextStorage.ACCOUNT_ID_KEY);
  }

  setSelectedAccountId(accountId: string, strategyStyle: StrategyStyleType): void {
    if (accountId !== AccountContextStorage.ALL_ACCOUNTS_ID) {
      this.sessionStorageService.setItem(AccountContextStorage.ACCOUNT_ID_KEY, accountId);
    }
    if (strategyStyle === 'all-accounts' ||
      AccountContextStorage.ALL_ACCOUNTS_ID !== this.getLocalStorageAccountId()) {
      this.localStorageService.setItem(AccountContextStorage.ALL_ACCOUNT_ID_KEY, accountId);
    }
  }

  getLocalStorageAccountId(): string {
    return this.localStorageService.getItem<string>(AccountContextStorage.ALL_ACCOUNT_ID_KEY);
  }

  getSelectedAccountGroupId(): string {
    return this.sessionStorageService.getItem<string>(AccountContextStorage.ACCOUNT_GROUP_ID_KEY);
  }

  setSelectedAccountGroupId(groupId: string): void {
    if (AccountContextStorage.ALL_ACCOUNTS_ID !== groupId) {
      this.sessionStorageService.setItem(AccountContextStorage.ACCOUNT_GROUP_ID_KEY, groupId);
    }
  }

  isAllAccountId(account: Account): boolean {
    return account !== undefined && account.id === AccountContextStorage.ALL_ACCOUNTS_ID;
  }

}

abstract class BaseAccountResolver {

  protected constructor(protected accountContextStorage: AccountContextStorage) {
  }

  protected getLastSelectedAccount(accounts: Array<Account>, fromLocalStorage: boolean = false): Account {
    const accountId = this.accountContextStorage.getSelectedAccountId(fromLocalStorage);
    return accountId === undefined ? undefined : this.getAccountById(accounts, accountId);
  }

  protected getLastSelectedAccountGroup(accountGroups: Array<AccountGroup>): AccountGroup {
    const accountGroupId = this.accountContextStorage.getSelectedAccountGroupId();
    let accountGroup;
    if (accountGroupId !== undefined) {
      accountGroup = this.getAccountGroupById(accountGroups, accountGroupId);
    }
    return accountGroup;
  }

  protected getFavoriteAccount(accounts: Array<Account>): Account {
    return accounts.find((account: Account): boolean => account.favorite);
  }

  protected getFavoriteAccountGroup(accountGroups: Array<AccountGroup>): AccountGroup {
    return accountGroups.find((accountGroup: AccountGroup): boolean => accountGroup.favorite);
  }

  protected getAccountById(accounts: Array<Account>, accountId: string): Account {
    return accounts.find((account: Account): boolean => accountId === account.id);
  }

  protected getAccountGroupById(accountGroups: Array<AccountGroup>, accountGroupId: string): AccountGroup {
    return accountGroups.find((accountGroup: AccountGroup): boolean => accountGroup.groupId === accountGroupId);
  }

}

class LastSelectedAccountResolver extends BaseAccountResolver implements AccountResolver, AccountGroupResolver {

  constructor(accountContextStorage: AccountContextStorage) {
    super(accountContextStorage);
  }

  resolveAccount(accounts: Array<Account>, accountId: string): Account {
    let account = this.getAccountById(accounts, accountId);
    if (account === undefined) {
      account = this.getLastSelectedAccount(accounts);
    }
    if (account === undefined) {
      account = this.getFavoriteAccount(accounts);
    }
    if (account === undefined) {
      account = accounts[0];
    }
    return account;
  }

  resolveAccountGroup(accountGroups: Array<AccountGroup>, accountGroupId: string): AccountGroup {
    let accountGroup = this.getAccountGroupById(accountGroups, accountGroupId);
    if (accountGroup === undefined) {
      accountGroup = this.getLastSelectedAccountGroup(accountGroups);
    }
    if (accountGroup === undefined) {
      accountGroup = this.getFavoriteAccountGroup(accountGroups);
    }
    if (accountGroup === undefined) {
      accountGroup = accountGroups[0];
    }
    return accountGroup;
  }
}

class DefaultAccountResolver extends BaseAccountResolver implements AccountResolver, AccountGroupResolver {

  constructor(accountContextStorage: AccountContextStorage) {
    super(accountContextStorage);
  }

  resolveAccount(accounts: Array<Account>, accountId: string): Account {
    let account = this.getAccountById(accounts, accountId);
    if (account === undefined) {
      account = this.getFavoriteAccount(accounts);
    }
    if (account === undefined) {
      account = accounts[0];
    }
    return account;
  }

  resolveAccountGroup(accountGroups: Array<AccountGroup>, accountGroupId: string): AccountGroup {
    let accountGroup = this.getAccountGroupById(accountGroups, accountGroupId);
    if (accountGroup === undefined) {
      accountGroup = this.getFavoriteAccountGroup(accountGroups);
    }
    if (accountGroup === undefined) {
      accountGroup = accountGroups[0];
    }
    return accountGroup;
  }
}

class LastSelectedNoFallbackAccountResolver extends BaseAccountResolver implements AccountResolver {

  constructor(accountContextStorage: AccountContextStorage) {
    super(accountContextStorage);
  }

  resolveAccount(accounts: Array<Account>, accountId: string): Account {
    let account = this.getAccountById(accounts, accountId);
    if (account === undefined) {
      const selectedAccountId = this.accountContextStorage.getSelectedAccountId();
      account = selectedAccountId === undefined || selectedAccountId === null ?
        this.getFavoriteAccount(accounts) : this.getLastSelectedAccount(accounts);
    }
    return account;
  }
}

class DefaultAccountNoFallbackAccountResolver extends BaseAccountResolver implements AccountResolver {

  constructor(accountContextStorage: AccountContextStorage) {
    super(accountContextStorage);
  }

  resolveAccount(accounts: Array<Account>, accountId: string): Account {
    let account = this.getAccountById(accounts, accountId);
    if (account === undefined) {
      account = this.getFavoriteAccount(accounts);
    }
    return account;
  }
}

class AlwaysSelectAccountResolver extends BaseAccountResolver implements AccountResolver {

  constructor(accountContextStorage: AccountContextStorage) {
    super(accountContextStorage);
  }

  resolveAccount(accounts: Array<Account>, accountId: string): Account {
    return this.getAccountById(accounts, accountId);
  }
}

class AllAccountsAccountResolver extends BaseAccountResolver implements AccountResolver {

  constructor(accountContextStorage: AccountContextStorage) {
    super(accountContextStorage);
  }

  resolveAccount(accounts: Array<Account>, accountId: string): Account {
    // get value from local storage
    let account = this.getLastSelectedAccount(accounts, true);
    if (account === undefined || !this.accountContextStorage.isAllAccountId(account)) {
      // All accounts override what is in request parameter in accountId
      account = this.getAccountById(accounts, accountId);
    }
    if (account === undefined) {
      account = this.getLastSelectedAccount(accounts);
    }
    if (account === undefined) {
      account = this.getFavoriteAccount(accounts);
    }
    if (account === undefined) {
      account = accounts[0];
    }
    return account;
  }
}

/**
 * Account Context Service
 * Injectable service to provide Account and AccountGroup resolution functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AccountContextService {

  private accountContextStorage: AccountContextStorage;
  private accountResolverFactory: AccountResolverFactory;

  constructor(preferencesService: PreferencesService,
              sessionStorageService: SessionStorageService,
              localStorageService: LocalStorageService) {
    this.accountContextStorage = new AccountContextStorage(sessionStorageService, localStorageService);
    this.accountResolverFactory = new AccountResolverFactory(preferencesService, this.accountContextStorage);
  }

  getSelectedAccountId(): string {
    return this.accountContextStorage.getSelectedAccountId();
  }

  setSelectedAccountId(accountId: string, strategyStyle: StrategyStyleType = undefined): void {
    this.accountContextStorage.setSelectedAccountId(accountId, strategyStyle);
  }

  getSelectedAccountGroupId(): string {
    return this.accountContextStorage.getSelectedAccountGroupId();
  }

  setSelectedAccountGroupId(groupId: string): void {
    this.accountContextStorage.setSelectedAccountGroupId(groupId);
  }

  /**
   * resolveAccount
   * Resolves selected Account
   *
   * @param accounts
   * @param accountId
   * @param strategyStyle
   * @return Account
   */
  resolveAccount(accounts: Array<Account>, accountId: string, strategyStyle: StrategyStyleType): Observable<Account> {
    return this.accountResolverFactory.getAccountResolver(strategyStyle).pipe(
      map((accountResolver: AccountResolver): Account => accountResolver.resolveAccount(accounts, accountId))
    );
  }

  /**
   * resolveAccountGroup
   * Resolves selected AccountGroup
   *
   * @param accountGroups
   * @param accountGroupId
   *
   * @return Account Group
   */
  resolveAccountGroup(accountGroups: Array<AccountGroup>, accountGroupId: string): Observable<AccountGroup> {
    return this.accountResolverFactory.getAccountGroupResolver().pipe(
      map((resolver: AccountGroupResolver): AccountGroup => resolver.resolveAccountGroup(accountGroups, accountGroupId))
    );
  }

  initSelectedAccount(accounts: Array<Account>, strategyStyle: StrategyStyleType = 'default', accountContextUpdate?: boolean,
                      initAccountId?: string, groupOverridesAccountContext?: boolean): Observable<Account> {
    return this.resolveAccount(accounts, initAccountId, strategyStyle)
      .pipe(map((account: Account): Account => {
        let selectedGroupId;
        if (account !== undefined) {
          if (groupOverridesAccountContext) {
            selectedGroupId = this.getSelectedAccountGroupId();
            if (account.groupId !== selectedGroupId) {
              account = accounts
                .reduceRight((previous: Account, current: Account): Account => {
                  const matchingGroupId = current.groupId === this.getSelectedAccountGroupId();
                  return matchingGroupId ? current : previous;
                }, accounts[0]);
            }
          }
          if (accountContextUpdate && account !== undefined && account.id !== undefined && account.groupId !== undefined) {
            this.setSelectedAccountId(account.id, strategyStyle);
            this.setSelectedAccountGroupId(account.groupId);
          }
          return account;
        } else {
          // TODO vpopov do we need it?
          return undefined;
        }
      }));
  }

}
